import React, { useEffect, useState } from "react";
import {
  listParokiaWithAdmins,
  createParokia,
  getJimbos,
  assignParokiaAdmin,
  resetParokiaAdminPassword,
  updateParokia,
  createFacilityId
} from "../../services/api";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./hierarchy.css";

export default function ParokiaPage() {
  const [parokia, setParokia] = useState([]);
  const [jimbos, setJimbos] = useState([]);
  const [loading, setLoading] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const [selectedParokia, setSelectedParokia] = useState(null);

  /* ================= ADMIN STATES ================= */
  const [assignOpen, setAssignOpen] = useState(false);
  const [adminForm, setAdminForm] = useState({
    firstName: "",
    lastName: "",
    username: "",
    password: ""
  });
  const [hasEditedUsername, setHasEditedUsername] = useState(false);
  const [resetRowId, setResetRowId] = useState(null);
  const [rowPasswords, setRowPasswords] = useState({
    password: "",
    confirmPassword: ""
  });

/* ================= AUTO-GENERATE USERNAME ================= */
useEffect(() => {
  const { firstName, lastName } = adminForm;
  if (firstName || lastName) {
    const generatedUsername = `${lastName}${firstName?.[0] || ""}`.toLowerCase();
    setAdminForm(prev => ({ ...prev, username: generatedUsername }));
  }
}, [adminForm.firstName, adminForm.lastName]);

  /* ================= PAROKIA FORM ================= */
  const initialForm = {
    jimbo_id: "",
    name: "",
    location: "",
    paroko_name: "",
    mwenyekiti: "",
    katibu: "",
    mweka_hazina: "",
    bank_reference: "",
    status: "ACTIVE",
  };

  const [form, setForm] = useState(initialForm);

  /* ================= LOAD DATA ================= */
  useEffect(() => {
    loadParokia();
    loadJimbos();
  }, []);

  const loadParokia = async () => {
    setLoading(true);
    try {
      const res = await listParokiaWithAdmins();
      setParokia(Array.isArray(res) ? res : res.data || []);
    } catch {
      toast.error("Failed to load Parokia");
    }
    setLoading(false);
  };

  const loadJimbos = async () => {
    try {
      const res = await getJimbos();
      setJimbos(Array.isArray(res) ? res : res.data || []);
    } catch {
      toast.error("Failed to load Jimbos");
    }
  };

  /* ================= CREATE / UPDATE ================= */
    const handleCreate = async () => {
    if (!form.name || !form.jimbo_id) {
      toast.error("Name and Jimbo are required");
      return;
    }

    if (!window.confirm("Create this Parokia?")) return;

    try {
      // 1️⃣ CREATE PAROKIA
      const res = await createParokia(form);

      const parokiaId = res?.parokiaId || res?.data?.parokiaId;

      toast.success("Parokia created successfully");

      setCreateOpen(false);
      setForm(initialForm);

      // 2️⃣ IMMEDIATELY GENERATE FACILITY ID
      if (parokiaId) {
        await createFacilityId(parokiaId);
      }

      // 3️⃣ RELOAD LIST AFTER UPDATE
      loadParokia();

    } catch (err) {
      toast.error("Failed to create Parokia");
    }
  };

  const handleUpdate = async () => {
    if (!window.confirm("Save changes?")) return;

    try {
      await updateParokia(selectedParokia.parokia_id, form);
      toast.success("Parokia updated successfully");
      setEditMode(false);
      setViewOpen(false);
      loadParokia();
    } catch {
      toast.error("Failed to update Parokia");
    }
  };

  /* ================= ASSIGN ADMIN ================= */
  const handleAssignAdmin = async () => {
    const { firstName, lastName, username, password } = adminForm;

    if (!firstName || !lastName || !username || !password) {
      toast.error("All fields are required");
      return;
    }

    if (!window.confirm("Create Parokia Admin?")) return;

    try {
      await assignParokiaAdmin({
        parokiaId: selectedParokia.parokia_id,
        firstName,
        lastName,
        username,
        password
      });

      toast.success("Admin created successfully");
      setAssignOpen(false);
      setAdminForm({ firstName: "", lastName: "", username: "", password: "" });
      loadParokia();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to create admin");
    }
  };

  /* ================= INLINE RESET PASSWORD ================= */
  const handleInlineResetPassword = async (adminId) => {
    const { password, confirmPassword } = rowPasswords;

    if (!password || !confirmPassword) {
      toast.error("Both password fields are required");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (!window.confirm("Reset this admin password?")) return;

    try {
      await resetParokiaAdminPassword({ adminId, password });
      toast.success("Password reset successfully");
      setResetRowId(null);
      setRowPasswords({ password: "", confirmPassword: "" });
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to reset password");
    }
  };

  /* ================= VIEW ================= */
  const openView = (p) => {
    setSelectedParokia(p);
    setForm({ ...initialForm, ...p });
    setEditMode(false);
    setViewOpen(true);
  };

  const closeModal = () => {
    setCreateOpen(false);
    setViewOpen(false);
    setEditMode(false);
    setAssignOpen(false);
    setForm(initialForm);
    setResetRowId(null);
  };

  /* ================= FORM RENDER ================= */
  const renderFormFields = () => (
    <>
      <div className="modal-section">
        <h4>Basic Information</h4>
        <div className="form-grid">
          <select
            className="input"
            value={form.jimbo_id}
            onChange={(e) => setForm({ ...form, jimbo_id: e.target.value })}
          >
            <option value="">Select Jimbo</option>
            {jimbos.map((j) => (
              <option key={j.jimbo_id} value={j.jimbo_id}>{j.name}</option>
            ))}
          </select>
          <input className="input" placeholder="Parokia Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <input className="input" placeholder="Location"
            value={form.location}
            onChange={(e) => setForm({ ...form, location: e.target.value })}
          />
          <input className="input" placeholder="Bank Reference"
            value={form.bank_reference}
            onChange={(e) => setForm({ ...form, bank_reference: e.target.value })}
          />
        </div>
      </div>

      <div className="modal-section">
        <h4>Leadership (Kamati Tendaji)</h4>
        <div className="form-grid">
          <input className="input" placeholder="Paroko"
            value={form.paroko_name}
            onChange={(e) => setForm({ ...form, paroko_name: e.target.value })}
          />
          <input className="input" placeholder="Mwenyekiti"
            value={form.mwenyekiti}
            onChange={(e) => setForm({ ...form, mwenyekiti: e.target.value })}
          />
          <input className="input" placeholder="Katibu"
            value={form.katibu}
            onChange={(e) => setForm({ ...form, katibu: e.target.value })}
          />
          <input className="input" placeholder="Mweka Hazina"
            value={form.mweka_hazina}
            onChange={(e) => setForm({ ...form, mweka_hazina: e.target.value })}
          />
        </div>
      </div>
    </>
  );

  /* ================= VIEW RENDER ================= */
  const renderViewInfo = () => (
    <>
      <div className="form-grid">
        <div>
          <p><strong>Name:</strong> {selectedParokia?.parokia_name}</p>
          <p><strong>Location:</strong> {selectedParokia?.location || "-"}</p>
          <p><strong>Paroko:</strong> {selectedParokia?.paroko_name || "-"}</p>
          <p><strong>Bank Ref:</strong> {selectedParokia?.bank_reference || "-"}</p>
        </div>
        <div>
          <p><strong>Mwenyekiti:</strong> {selectedParokia?.mwenyekiti || "-"}</p>
          <p><strong>Katibu:</strong> {selectedParokia?.katibu || "-"}</p>
          <p><strong>Mweka Hazina:</strong> {selectedParokia?.mweka_hazina || "-"}</p>
          <p><strong>Status:</strong> {selectedParokia?.status}</p>
        </div>
      </div>

      <hr style={{ margin: "20px 0" }} />

      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <h4>Parokia Admins</h4>
        <button className="btn btn-green small" onClick={() => setAssignOpen(true)}>
          + Assign Admin
        </button>
      </div>

      {assignOpen && (
        <div className="assign-admin-form">
          <input
            className="input"
            placeholder="First Name"
            value={adminForm.firstName}
            onChange={(e) => setAdminForm({ ...adminForm, firstName: e.target.value })}
          />
          <input
            className="input"
            placeholder="Last Name"
            value={adminForm.lastName}
            onChange={(e) => setAdminForm({ ...adminForm, lastName: e.target.value })}
          />
          <input
              className="input"
              placeholder="Username"
              value={adminForm.username}
              readOnly
            />
          <input
            className="input"
            type="password"
            placeholder="Password"
            value={adminForm.password}
            onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })}
          />
          <button className="btn primary" onClick={handleAssignAdmin}>Assign Admin</button>
        </div>
      )}

      {selectedParokia?.admins?.length > 0 ? (
        <table className="table">
          <thead>
            <tr>
              <th>#</th>
              <th>Username</th>
              <th>Name</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {selectedParokia.admins.map((admin, i) => (
              <React.Fragment key={admin.user_id}>
                <tr>
                  <td>{i + 1}</td>
                  <td>{admin.username}</td>
                  <td>{admin.first_name} {admin.last_name}</td>
                  <td>{admin.status}</td>
                  <td>
                    <button
                      className="btn small"
                      onClick={() => setResetRowId(resetRowId === admin.user_id ? null : admin.user_id)}
                    >
                      Reset Password
                    </button>
                  </td>
                </tr>

                {resetRowId === admin.user_id && (
                  <tr>
                    <td colSpan="5">
                      <div style={{ display: "flex", gap: "10px" }}>
                        <input
                          type="password"
                          className="input"
                          placeholder="New Password"
                          value={rowPasswords.password}
                          onChange={(e) => setRowPasswords({ ...rowPasswords, password: e.target.value })}
                        />
                        <input
                          type="password"
                          className="input"
                          placeholder="Confirm Password"
                          value={rowPasswords.confirmPassword}
                          onChange={(e) => setRowPasswords({ ...rowPasswords, confirmPassword: e.target.value })}
                        />
                        <button
                          className="btn primary small"
                          onClick={() => handleInlineResetPassword(admin.user_id)}
                        >
                          Submit
                        </button>
                        <button
                          className="btn small"
                          onClick={() => {
                            setResetRowId(null);
                            setRowPasswords({ password: "", confirmPassword: "" });
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      ) : (
        <p>No admins assigned.</p>
      )}
    </>
  );

  /* ================= UI ================= */
  return (
    <div className="page-container">
      <div className="hierarchy-header">
        <div>
          <h1>Parokia Management</h1>
          <p className="subtitle">Manage parokia and leadership structure</p>
        </div>
        <button className="btn primary" onClick={() => setCreateOpen(true)}>+ New Parokia</button>
      </div>

      <div className="card">
        {loading ? (
          <div className="spinner" />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>#</th>
                <th>Facility ID</th>
                <th>Parokia</th>
                <th>Jimbo</th>
                <th>Paroko</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {parokia.map((p, i) => (
                <tr key={p.parokia_id} onDoubleClick={() => openView(p)} style={{ cursor: "pointer" }}>
                  <td>{i + 1}</td>
                  <td className="bold">{p.facility_id || "-"}</td>
                  <td className="bold">{p.parokia_name}</td>
                  <td>{p.jimbo_name}</td>
                  <td>{p.paroko_name || "-"}</td>
                  <td><span className="badge-success">{p.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {(createOpen || viewOpen) && (
        <div className="modal-overlay">
          <div className="modal" style={{ width: "95%", maxWidth: "1200px", height: "90vh" }}>
            <div className="modal-close" onClick={closeModal}>✕</div>
            <h3>{createOpen ? "Create Parokia" : editMode ? "Edit Parokia" : selectedParokia?.parokia_name}</h3>
            <div style={{ overflowY: "auto", height: "75%" }}>
              {(createOpen || editMode) ? renderFormFields() : renderViewInfo()}
            </div>
            <div className="modal-actions">
              {!createOpen && !editMode && <button className="btn" onClick={() => setEditMode(true)}>Edit</button>}
              <button className="btn" onClick={closeModal}>Cancel</button>
              {(createOpen || editMode) && <button className="btn primary" onClick={createOpen ? handleCreate : handleUpdate}>{createOpen ? "Create" : "Save Changes"}</button>}
            </div>
          </div>
        </div>
      )}

      {/* <ToastContainer position="top-right" autoClose={3000} /> */}
    </div>
  );
}